import type { TemplateName } from './core/assetType.js';
import type { RegistryData } from './core/registries.js';
import type { ContentAssetEntry } from './registrySchemas/contentAssets.js';

import { activityCatalogue, activitySetupJsonSchema } from './activities/index.js';
import { TEMPLATES_FIELD } from './casting/characterMatch.js';
import { CHARACTER_ETHNICITIES, CHARACTER_NAMES, NAME_GENDERS } from './casting/characterNames.js';
import { buildRegistries } from './core/registries.js';
import { BACKGROUND_GRADIENTS, CARD_COLORS, CARD_ILLUSTRATIONS, RENDERER_WALLET_KEYS, WALLET_KEYS } from './schemas/endingConstants.js';
import { GAMESHOW_QUIZ_ACTIVITY_TYPES } from './schemas/gameshowConstants.js';
import { KNOWLEDGE_CHECK_ACTIVITY_TYPES } from './schemas/lesson.js';
import { CHARACTER_ROLES } from './schemas/storyData.js';

const code = (value: string): string => `\`${value}\``;
const jsonBlock = (value: unknown): string => `\`\`\`json\n${JSON.stringify(value, null, 2)}\n\`\`\``;

/** Every activity type with its one-line summary, as a markdown list. */
export const activityMenuMarkdown = (): string =>
  activityCatalogue().map(entry => `- ${code(entry.type)} — ${entry.summary}`).join('\n');

/** The setup JSON Schema of every type a producer may choose under a template, one `####` heading per type. */
export const activitySchemasMarkdown = (templateName?: TemplateName): string =>
  activityCatalogue()
    .map(({ type }) => `#### ${code(type)}\n\n${jsonBlock(activitySetupJsonSchema(type, templateName ? { templateName } : {}))}`)
    .join('\n\n');

const TEST_STEP_TYPES: Partial<Record<TemplateName, readonly string[]>> = {
  gameshow: GAMESHOW_QUIZ_ACTIVITY_TYPES,
  lesson: [...KNOWLEDGE_CHECK_ACTIVITY_TYPES],
};

/** What an entry says about itself beyond its slug, kind and templates. */
const describe = (entry: ContentAssetEntry): string => {
  const parts = Object.entries(entry)
    .filter(([key, value]) => !['slug', 'kind', 'templates'].includes(key) && !(key === 'name' && value === entry.slug))
    .filter(([, value]) => value !== null && value !== '' && !(Array.isArray(value) && value.length === 0))
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`);
  return parts.length ? ` — ${parts.join('; ')}` : '';
};

const entryList = (entries: readonly ContentAssetEntry[]): string =>
  entries.length ? entries.map(entry => `- ${code(entry.slug)}${describe(entry)}`).join('\n') : 'None for this template.';

/**
 * Everything a story skill has to choose from under one template, read from the validator
 * and the registries it checks against: the activity types and their setup schemas, the
 * skills, consideration areas, career clusters, backgrounds and faces the template may use,
 * the character names, and the ending vocabularies. A skill reads this instead of keeping a
 * copy, so it is never out of date.
 */
export const renderReference = (templateName: TemplateName, data: RegistryData): string => {
  const registries = buildRegistries(data);
  const sets = registries.contentAssets[templateName];
  const backgrounds = data.contentAssets.backgrounds.filter(entry => sets.backgrounds.has(entry.slug));
  const characters = [...sets.charactersBySlug.values()];
  const characterFamily = registries.contentAssets.families.get('character');
  const testStep = TEST_STEP_TYPES[templateName];

  const sections = [
    `# ${templateName} reference`,
    'Generated from `packages/content-validator` and the registries it checks against. Read it; never copy it into a skill.',
    '## Skills (`fields.indexing.skillsList`)',
    [...registries.skills.values()].map(skill => `- ${code(skill.slug)} — ${skill.name}${skill.description ? `: ${skill.description}` : ''}`).join('\n'),
    '## Consideration areas (`fields.considerationAreaSlugs`, exactly one)',
    [...registries.considerationAreas].map(code).join(', '),
    '## Career clusters (`fields.indexing.careerCluster`)',
    registries.careerClusters ? [...registries.careerClusters].map(code).join(', ') : 'No list is supplied: write the closest kebab-case cluster slug.',
    '## Backgrounds (`defaultBackgroundContentAssetSlug`, a node\'s `backgroundContentAssetSlug`)',
    entryList(backgrounds),
    '## Faces (`characters[].contentAssetSlug`, `narratorContentAssetSlug`)',
    `A character's \`role\` is one of ${CHARACTER_ROLES.map(code).join(', ')}; \`self\` is the player. A face with nothing after its slug is undescribed: choose it by the words in its slug, and name the character to fit the part.`,
    entryList(characters),
    characterFamily
      ? `### How a character request (\`characterSchema\`) is written\n\n${characterFamily.fields.filter(field => field.name !== TEMPLATES_FIELD).map(field => `- ${code(field.name)} (${field.type})${field.options.length ? `: ${field.options.map(code).join(', ')}` : ''}`).join('\n')}`
      : '',
    '## Character names, by background and gender',
    CHARACTER_ETHNICITIES.map(ethnicity => `- ${code(ethnicity)}: ${NAME_GENDERS.map(gender => `${gender} ${CHARACTER_NAMES[ethnicity][gender].join(', ')}`).join(' · ')}`).join('\n'),
    '## Endings (result quizzes only: `walletUpdates`, `ending:wallet`, `ending:rich`)',
    [
      `- Wallet keys (\`walletUpdates\`): ${WALLET_KEYS.map(code).join(', ')}; with their own result screen: ${RENDERER_WALLET_KEYS.map(code).join(', ')}`,
      `- Rich-ending gradients: ${BACKGROUND_GRADIENTS.map(code).join(', ')}`,
      `- Rich-ending card colours: ${CARD_COLORS.map(code).join(', ')}; card icons: ${CARD_ILLUSTRATIONS.map(code).join(', ')}`,
    ].join('\n'),
    '## Activity types',
    activityMenuMarkdown(),
    testStep ? `A ${templateName === 'gameshow' ? 'round' : 'test step'} asks with one of: ${testStep.map(code).join(', ')}.` : '',
    '## Setup schema for each type',
    activitySchemasMarkdown(templateName),
  ];
  return `${sections.filter(section => section !== '').join('\n\n')}\n`;
};
